import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { throwError, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TokenStorage } from '../authentication/token-storage.service';

import { CreateSpaceDto } from '../partials/modals/create-space-modal/dto/create-space.dto';
import {
  ProjectItem,
  TransactionTypeEnum,
  OutlineItem,
} from '../models/base.models';
import { CreateWalletDto } from '../partials/modals/create-space-wallet-modal/dto/create-wallet.dto';
import { CreateTransactionDto } from '../partials/modals/create-space-transaction-modal/dto/create-transaction.dto';
import { LoginDTO, SignupDTO } from '../authentication/dto/login.dto';
import Swal from 'sweetalert2';
import { CreateProjectDto } from 'src/app/components/user/projects/dto/project.create.dto';
import { CreateProjectContentDto } from 'src/app/components/user/projects/project-item/project-dashboard/project-outline/dto/content.create.dto';
export class ErrorModifiedType extends HttpErrorResponse {
  override error: ErrorBody;
}

export class ErrorBody {

  error: string; message: string; statusCode: number
}
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private apiUrl = environment.apiUrl;
  private headers = new HttpHeaders();

  constructor(private http: HttpClient, private tokenStorage: TokenStorage) {
    // Set up any common headers if needed
    // this.headers = this.headers.append('Authorization', `Bearer ${tokenStorage.getAccessToken()}`);
    this.headers = this.headers.append('Content-Type', 'application/json');
  }

  // Auth Functions

  login(loginData: LoginDTO, errorHandler: any = this.handleError) {
    return this.post(`auth/login`, loginData, errorHandler);
  }

  register(signupData: SignupDTO, errorHandler: any = this.handleError) {
    return this.post(`auth/signup`, signupData, errorHandler);
  }


  getProfile(errorHandler: any = this.handleError) {
    return this.get(`auth/me`, null, errorHandler);
  }


  createProject(
    payload: CreateProjectDto,
    errorHandler: any = this.handleError
  ): Observable<{project: ProjectItem}> {
    return this.post(`projects`, payload, errorHandler);
  }

  getProjects(errorHandler: any = this.handleError) {
    return this.get(`projects`, null, errorHandler);
  }

  getProjectById(id: number, errorHandler: any = this.handleError) {
    return this.get(`projects/${id}`, null, errorHandler);
  }

  updateProjectById(id: number, payload: Partial<CreateProjectDto>, errorHandler: any = this.handleError) {
    return this.put(`projects/${id}`, payload, errorHandler);
  }


  // Project Content

  getProjectContentById(id: number, errorHandler: any = this.handleError) {
    return this.get(`projects/${id}/content`, null, errorHandler);
  }

  createProjectContent(id: number, payload: CreateProjectContentDto, errorHandler: any = this.handleError) {
    return this.post(`projects/${id}/content`, payload, errorHandler);
  }

  applyProjectContentTemplate(id: number, payload: {contents: OutlineItem[]}, errorHandler: any = this.handleError) {
    return this.post(`projects/${id}/content/outline`, payload, errorHandler);
  }


  // Space Transactions
  getSpaceTransactions(spaceId: number, errorHandler: any = this.handleError) {
    return this.get(`space/${spaceId}/transaction`, null, errorHandler);
  }

  createTransaction(
    spaceId: number,
    payload: CreateTransactionDto,
    errorHandler: any = this.handleError
  ) {
    return this.post(`space/${spaceId}/transaction`, payload, errorHandler);
  }

  // createSpaceWallet(spaceId: number, payload:CreateWalletDto ,errorHandler: any = this.handleError): Observable<WalletItem> {
  //   return this.post(`space/${spaceId}/wallet`, payload, errorHandler);
  // }

  // Space Category
  getSpaceCategories(
    spaceId: number,
    type: TransactionTypeEnum = TransactionTypeEnum.income,
    errorHandler: any = this.handleError
  ) {
    return this.get(
      `space/${spaceId}/category?type=${type}`,
      null,
      errorHandler
    );
  }

  // Space Contacts
  getSpaceContacts(spaceId: number, errorHandler: any = this.handleError) {
    return this.get(`space/${spaceId}/contact`, null, errorHandler);
  }

  // Space Wallets
  getSpaceWallets(spaceId: number, errorHandler: any = this.handleError) {
    return this.get(`space/${spaceId}/wallet`, null, errorHandler);
  }

  createSpaceWallet(
    spaceId: number,
    payload: CreateWalletDto,
    errorHandler: any = this.handleError
  ): Observable<OutlineItem> {
    return this.post(`space/${spaceId}/wallet`, payload, errorHandler);
  }

  // Currencies
  getCurrencies(errorHandler: any = this.handleError) {
    return this.get(`currency`, null, errorHandler);
  }

  // ------------------------------ Helper Methods ----------------------------
  // Function to handle HTTP errors
  private requestErrorAlert = (error: ErrorBody) => {
    Swal.fire({
      title: 'Oops, Something went wrong!',
      html: `
      Application has encountered an error.
      <ul class="text-start mt-4 mb-0">
        <li><strong>Message Head:</strong> ${error.message}</li>
        <li><strong>Error Message:</strong> ${error.error}</li>
        <li><strong>Code:</strong> ${error.statusCode}</li>
      </ul>
      `,
      confirmButtonText: 'Go To Home',
      allowOutsideClick: true,
      allowEscapeKey: true,
      showCancelButton: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn btn-danger',
      },
      icon: 'error',
    }).then((result) => {
      if (result.isConfirmed) {
        // document.location.href = environment.basePath;
      }
    });
  };

  private handleError = (error: ErrorModifiedType) => {
    // let errorBody: {
    //   error: string;
    //   message: string;
    //   statusCode: number;
    // } = error.error;

    console.error('API SERVICE ERROR: ', error);
    // if (!environment?.production && !!environment.appSettings?.errors?.displayPopAlert) {
    // this.requestErrorAlert(error.error);
    // }
    return throwError(() => error);
  };

  // Function to make GET requests
  get(path: string, params: any, errorHandler?: any): Observable<any> {
    return this.call('GET', path, params, errorHandler);
  }

  // Function to make POST requests
  post(path: string, data: any, errorHandler?: any): Observable<any> {
    return this.call('POST', path, data, errorHandler);
  }

  // Function to make PUT requests
  put(path: string, data: any, errorHandler?: any): Observable<any> {
    return this.call('PUT', path, data, errorHandler);
  }

  // Function to make DELETE requests
  delete(path: string, errorHandler?: any): Observable<any> {
    return this.call('DELETE', path, null, errorHandler);
  }

  // Function to make PATCH requests
  patch(path: string, data: any, errorHandler?: any): Observable<any> {
    return this.call('PATCH', path, data, errorHandler);
  }

  // Function to make custom HTTP requests with specific headers
  call(
    method: string,
    path: string,
    params: any,
    errorHandler: any
  ): Observable<any> {
    if (!errorHandler) errorHandler = this.handleError;
    // console.debug(`LSG API request '${method}'`, `${ this.apiUrl }/${ path }`, params);
    return this.http
      .request<any>(method, `${this.apiUrl}/${path}`, {
        body: params,
        headers: this.headers,
      })
      .pipe(
        map((resp) => {
          if (resp?.error) {
            throw resp?.error;
          }
          return resp;
        }),
        catchError(errorHandler)
      );
  }
}
